/**
 * Admin Chat Widget
 * A quick "support inbox" view of all chat activity.
 * Shows recent messages across all channels (newest first).
 * Features: inline reply, deep-link to channel, seen/read logic, filtering.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  MessageSquare, Bell, Send, RefreshCw, Filter,
  ChevronLeft, ChevronRight, Reply, Check, ExternalLink, AtSign
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../ui/select';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useWS } from '../../contexts/WebSocketContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminChatWidget({ minimized = false }) {
  const navigate = useNavigate();
  const [filterType, setFilterType] = useState('all');
  const [messagesData, setMessagesData] = useState({ messages: [], page: 1, total_pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  
  const ws = useWS();

  const fetchMessages = useCallback(async (filter = 'all', pageNum = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/admin-dashboard/chat/all-messages?filter_type=${filter}&page=${pageNum}&limit=25`
      );
      setMessagesData(response.data);
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages(filterType, 1);
  }, [filterType, fetchMessages]);

  // Refresh on new WebSocket messages
  useEffect(() => {
    if (ws?.messages?.length > 0) {
      // Debounce refresh
      const timeout = setTimeout(() => {
        fetchMessages(filterType, page);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [ws?.messages?.length, filterType, page, fetchMessages]);

  const handleReply = async () => {
    if (!replyingTo || !replyText.trim()) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/admin-dashboard/chat/quick-reply`, {
        channel_id: replyingTo.channel_id,
        content: replyText.trim(),
        reply_to_message_id: replyingTo.id
      });
      
      toast.success('Reply sent!');
      setReplyText('');
      setReplyingTo(null);
      
      // Refresh messages
      fetchMessages(filterType, page);
      
      // Clear unread count in WebSocket context
      if (ws?.clearUnreadCount) {
        ws.clearUnreadCount(replyingTo.channel_id);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleMarkRead = async (channelId) => {
    try {
      await axios.post(`${API}/admin-dashboard/chat/mark-read`, {
        channel_id: channelId
      });
      
      // Clear unread count in WebSocket context
      if (ws?.clearUnreadCount) {
        ws.clearUnreadCount(channelId);
      }
      
      // Refresh if on unread tab
      if (filterType === 'unread') {
        fetchMessages('unread', page);
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Deep-link: open channel in full chat page
  const handleOpenChannel = (channelId, channelName) => {
    navigate(`/chat?channel=${channelId}`);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return '';
    }
  };

  if (minimized) {
    return (
      <div className="p-2 flex items-center gap-2 text-sm text-muted-foreground">
        <MessageSquare className="h-4 w-4" />
        <span>{messagesData.total} messages</span>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with filter and refresh */}
      <div className="flex items-center justify-between gap-2 mb-2 px-1">
        <div className="flex items-center gap-2">
          <Select value={filterType} onValueChange={(v) => { setFilterType(v); setPage(1); }}>
            <SelectTrigger className="h-7 w-[120px] text-xs">
              <Filter className="h-3 w-3 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" /> All
                </div>
              </SelectItem>
              <SelectItem value="unread">
                <div className="flex items-center gap-1">
                  <Bell className="h-3 w-3" /> Unread
                </div>
              </SelectItem>
              <SelectItem value="mentions" disabled>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <AtSign className="h-3 w-3" /> Mentions
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          <Badge variant="outline" className="text-xs">
            {messagesData.total}
          </Badge>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-7 w-7"
          onClick={() => fetchMessages(filterType, 1)} 
          disabled={loading}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Messages list */}
      <div className="flex-1 flex flex-col min-h-0">
        <ScrollArea className="flex-1">
          <div className="space-y-2 pr-2">
            {messagesData.messages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {filterType === 'unread' ? 'No unread messages' : 'No messages found'}
                </p>
              </div>
            ) : (
              messagesData.messages.map((msg) => (
                <div 
                  key={msg.id} 
                  data-testid={`admin-chat-message-${msg.id}`}
                  className={`p-2 rounded-lg border transition-colors ${
                    msg.is_from_admin 
                      ? 'bg-primary/5 border-primary/20' 
                      : 'bg-muted/30 border-transparent hover:border-muted-foreground/20'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <Avatar className="h-7 w-7 flex-shrink-0">
                      <AvatarImage src={msg.sender_profile_picture} />
                      <AvatarFallback className="text-xs">
                        {msg.sender_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{msg.sender_name}</span>
                        <Badge 
                          variant="outline" 
                          className="text-[10px] px-1 py-0 cursor-pointer hover:bg-accent"
                          onClick={() => handleOpenChannel(msg.channel_id, msg.channel_name)}
                        >
                          #{msg.channel_name}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">
                          {formatTime(msg.created_at)}
                        </span>
                      </div>
                      <p className="text-sm mt-1 whitespace-pre-wrap break-words">
                        {msg.content?.length > 150 
                          ? `${msg.content.substring(0, 150)}...` 
                          : msg.content}
                      </p>
                      
                      {/* Action buttons */}
                      <div className="flex items-center gap-1 mt-2">
                        {!msg.is_from_admin && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs"
                              data-testid={`reply-btn-${msg.id}`}
                              onClick={() => setReplyingTo(msg)}
                            >
                              <Reply className="h-3 w-3 mr-1" /> Reply
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs"
                              data-testid={`mark-read-btn-${msg.id}`}
                              onClick={() => handleMarkRead(msg.channel_id)}
                            >
                              <Check className="h-3 w-3 mr-1" /> Mark Read
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          data-testid={`open-channel-btn-${msg.id}`}
                          onClick={() => handleOpenChannel(msg.channel_id, msg.channel_name)}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" /> Open
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Reply input */}
        {replyingTo && (
          <div className="border-t pt-2 mt-2 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Replying to <span className="font-medium">{replyingTo.sender_name}</span> in #{replyingTo.channel_name}
              </p>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 text-xs"
                onClick={() => setReplyingTo(null)}
              >
                Cancel
              </Button>
            </div>
            <div className="flex gap-2">
              <Input
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply..."
                className="flex-1 h-8 text-sm"
                data-testid="admin-chat-reply-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleReply();
                  }
                }}
              />
              <Button 
                size="sm" 
                className="h-8"
                onClick={handleReply}
                disabled={!replyText.trim() || sending}
                data-testid="admin-chat-send-reply"
              >
                <Send className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}

        {/* Pagination */}
        {messagesData.total_pages > 1 && (
          <div className="flex items-center justify-between pt-2 border-t mt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchMessages(filterType, page - 1)}
              disabled={page <= 1 || loading}
              data-testid="admin-chat-prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page} of {messagesData.total_pages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchMessages(filterType, page + 1)}
              disabled={page >= messagesData.total_pages || loading}
              data-testid="admin-chat-next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminChatWidget;
